import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import * as entriesApi from '@/api/entries';
import type { EntryPatch } from '@/api/entries';
import { useOptimisticMutation, patchPaged, prependPaged, removeById, replaceById } from './optimistic';
import { tempId } from '@/lib/utils';
import { hoursShort } from '@/lib/format';
import type {
  EntryFilters,
  EntrySort,
  Paginated,
  PersonalHoursPeriod,
  TimeEntry,
  TimeEntryInput
} from '@/types';

export interface EntriesKeyInput {
  page: number;
  filters: EntryFilters;
  sort: EntrySort;
}

/**
 * The full query key, including filters and sort. Optimistic patches target
 * `qk.daycardsRoot` (a prefix) so a write updates every cached page/filter
 * combination at once rather than only the one on screen.
 */
export const entriesKey = (input: EntriesKeyInput) =>
  [...qk.daycardsRoot, input.page, input.filters, input.sort] as const;

export function useEntries(input: EntriesKeyInput, options: { original?: boolean } = {}) {
  return useQuery({
    queryKey: options.original ? ['original-daycards', input] : entriesKey(input),
    queryFn: ({ signal }) =>
      options.original
        ? entriesApi.fetchOriginalEntries(input, signal)
        : entriesApi.fetchEntries(input, signal),
    // Paging or re-sorting keeps the previous rows on screen instead of
    // blanking the table — v1 unmounted the whole tbody on every sort click.
    placeholderData: keepPreviousData
  });
}

type EntriesCache = Paginated<TimeEntry>;

export function useCreateEntry(currentUser: { id: string; name: string | null }) {
  return useOptimisticMutation<EntriesCache, TimeEntryInput, TimeEntry>({
    mutationFn: (input) => entriesApi.createEntry(input),
    errorMessage: 'That entry could not be saved.',
    optimistic: (input) => ({
      patches: [
        {
          key: qk.daycardsRoot,
          update: (cache: never, vars) => {
            const optimisticEntry: TimeEntry = {
              id: tempId('ent'),
              date: vars.date,
              clientId: vars.clientId,
              activityId: vars.activityId,
              userId: vars.userId ?? currentUser.id,
              time: vars.time,
              misc: vars.misc ?? null,
              hourlyRate: null,
              invoiced: vars.invoiced ?? false,
              invoiceId: null,
              userName: vars.userId ? null : currentUser.name,
              userEmail: null
            };
            // If the row wouldn't match the current filter, don't fake it in.
            const paged = cache as EntriesCache;
            if (input.invoiced && paged.items.some((i) => !i.invoiced) === false) return cache;
            return prependPaged(paged, optimisticEntry) as never;
          }
        }
      ],
      invalidate: [qk.daycardsRoot, qk.reportsRoot, qk.setup]
    }),
    // Swap the temp row for the real one so the id is correct for a follow-up
    // edit before the invalidation lands.
    onSuccessSideEffect(entry, _vars, qc) {
      qc.setQueriesData<EntriesCache>({ queryKey: qk.daycardsRoot }, (cache) =>
        cache
          ? {
              ...cache,
              items: cache.items.map((i) => (i.id.startsWith('tmp_') ? entry : i))
            }
          : cache
      );
    }
  });
}

export function useUpdateEntry() {
  return useOptimisticMutation<EntriesCache, { id: string; patch: EntryPatch }, TimeEntry>({
    mutationFn: ({ id, patch }) => entriesApi.updateEntry(id, patch),
    errorMessage: 'That change could not be saved.',
    optimistic: () => ({
      patches: [
        {
          key: qk.daycardsRoot,
          update: (cache: never, vars) =>
            patchPaged(cache as EntriesCache, (items) =>
              replaceById(items, vars.id, vars.patch as Partial<TimeEntry>)
            ) as never
        }
      ],
      invalidate: [qk.daycardsRoot, qk.reportsRoot]
    })
  });
}

/**
 * Toggling "invoiced" removes the row when the "hide invoiced" filter is on.
 * The row therefore has to disappear optimistically too, not just change state.
 */
export function useToggleEntryInvoiced(hideInvoiced: boolean) {
  return useOptimisticMutation<EntriesCache, { id: string; invoiced: boolean }, TimeEntry>({
    mutationFn: ({ id, invoiced }) => entriesApi.updateEntry(id, { invoiced }),
    errorMessage: 'Could not update that entry.',
    optimistic: () => ({
      patches: [
        {
          key: qk.daycardsRoot,
          update: (cache: never, vars) =>
            patchPaged(cache as EntriesCache, (items) =>
              hideInvoiced && vars.invoiced
                ? removeById(items, vars.id)
                : replaceById(items, vars.id, { invoiced: vars.invoiced })
            ) as never
        }
      ],
      invalidate: [qk.daycardsRoot]
    })
  });
}

export function useDeleteEntry() {
  return useOptimisticMutation<EntriesCache, { id: string }, unknown>({
    mutationFn: ({ id }) => entriesApi.deleteEntry(id),
    errorMessage: 'That entry could not be deleted.',
    optimistic: () => ({
      patches: [
        {
          key: qk.daycardsRoot,
          update: (cache: never, vars) =>
            patchPaged(cache as EntriesCache, (items) => removeById(items, vars.id)) as never
        }
      ],
      invalidate: [qk.daycardsRoot, qk.reportsRoot]
    }),
    successMessage: 'Entry deleted'
  });
}

export function useBulkDeleteEntries() {
  return useOptimisticMutation<EntriesCache, { ids: string[] }, unknown>({
    mutationFn: ({ ids }) => entriesApi.bulkDeleteEntries(ids),
    errorMessage: 'Those entries could not be deleted.',
    optimistic: () => ({
      patches: [
        {
          key: qk.daycardsRoot,
          update: (cache: never, vars) => {
            const ids = new Set(vars.ids);
            return patchPaged(cache as EntriesCache, (items) =>
              items.filter((i) => !ids.has(i.id))
            ) as never;
          }
        }
      ],
      invalidate: [qk.daycardsRoot, qk.reportsRoot]
    }),
    successMessage: (_r, vars) => `${vars.ids.length} entries deleted`
  });
}

// -------------------------------------------------------------------- timer

/**
 * The running timer is polled slowly as a safety net (it can be stopped from
 * the iOS app or the CLI) but the visible clock ticks locally, so the number
 * never freezes between polls.
 */
export function useTimer() {
  return useQuery({
    queryKey: qk.timer,
    queryFn: ({ signal }) => entriesApi.fetchTimer(signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: entriesApi.startTimer,
    async onMutate(vars) {
      await qc.cancelQueries({ queryKey: qk.timer });
      const previous = qc.getQueryData(qk.timer);
      const clients = qc.getQueryData<{ id: string; name: string }[]>(qk.clients) ?? [];
      const activities = qc.getQueryData<{ id: string; name: string }[]>(qk.activities) ?? [];
      qc.setQueryData(qk.timer, {
        id: tempId('tmr'),
        clientId: vars.clientId,
        clientName: clients.find((c) => c.id === vars.clientId)?.name ?? null,
        activityId: vars.activityId,
        activityName: activities.find((a) => a.id === vars.activityId)?.name ?? null,
        note: vars.note ?? null,
        startedAt: new Date().toISOString(),
        stoppedAt: null,
        elapsedSeconds: 0
      });
      return { previous };
    },
    onError(_e, _v, ctx) {
      qc.setQueryData(qk.timer, ctx?.previous ?? null);
      toast.error('Could not start the timer.');
    },
    onSettled() {
      void qc.invalidateQueries({ queryKey: qk.timer });
    }
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: entriesApi.stopTimer,
    async onMutate() {
      await qc.cancelQueries({ queryKey: qk.timer });
      const previous = qc.getQueryData(qk.timer);
      qc.setQueryData(qk.timer, null);
      return { previous };
    },
    onError(_e, _v, ctx) {
      qc.setQueryData(qk.timer, ctx?.previous ?? null);
      toast.error('Could not stop the timer.');
    },
    onSuccess(result) {
      if (result.entry) {
        toast.success(`Logged ${hoursShort(result.entry.time)}h`);
      }
      // v1 never did this, so a stopped timer's entry didn't appear until a
      // manual refresh.
      void qc.invalidateQueries({ queryKey: qk.daycardsRoot });
    },
    onSettled() {
      void qc.invalidateQueries({ queryKey: qk.timer });
    }
  });
}

// ----------------------------------------------------------- personal hours

export function usePersonalHours(userId: string, period: PersonalHoursPeriod) {
  return useQuery({
    queryKey: [...qk.personalHoursRoot, userId, period],
    queryFn: ({ signal }) => entriesApi.fetchPersonalHours({ userId, period }, signal),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData
  });
}
